export const BUILTIN_NOT_READY_STATES = [
  'NOT_READY',
  'WRAP_UP',
  'BREAK',
  'LUNCH',
  'AWAY',
  'DO_NOT_DISTURB',
  'BUSINESS_TRIP',
];

export const BUSY_STATES = ['OTHER_CALL', 'QUEUE_CALL'];

// Statuses that aren't agent-set queue statuses — system / wallboard-derived.
// Used to decide whether an unknown status should fall back to not-ready.
// NOTE: ONLINE is intentionally NOT here — it means the agent is registered on
// the phone but not signed into the queue, so it counts as not-ready and the UI
// must offer "sign in" (log in) rather than "sign out".
const SYSTEM_QUEUE_STATUSES = [
  'READY',
  'OFFLINE',
  'NO_ACCESS',
  'PARTIALLY_READY',
  'MIXED',
  'CHAT_ONLINE',
  'CHAT_BUSY',
  'CHAT_OFFLINE',
  ...BUSY_STATES,
];

export const getNotReadyStates = (customStatuses = []) => [
  ...BUILTIN_NOT_READY_STATES,
  ...customStatuses,
];

// Existing per-agent queue statuses that aren't in the configured list still
// behave as not-ready (we never block reading "unknown" historic values).
// System-derived statuses (READY, OFFLINE, MIXED, NO_ACCESS, …) never fall
// through to the not-ready bucket.
export const isNotReadyStatus = (status, customStatuses = []) => {
  if (!status) return false;
  if (BUILTIN_NOT_READY_STATES.includes(status)) return true;
  if (Array.isArray(customStatuses) && customStatuses.includes(status))
    return true;
  return !SYSTEM_QUEUE_STATUSES.includes(status);
};

// --- Call state on a conversation ------------------------------------------

// The flags in additional_attributes.phone arrive as real booleans from the API
// but as strings from some webhook payloads, so both forms count as true.
const isFlagSet = value => value === true || value === 'true';

export const getPhoneCallData = conversation =>
  conversation?.additional_attributes?.phone || {};

// An inbound call that rang but was never picked up by an agent. Single source
// of truth for the "missed call" look of a call (icon + preview text). The
// *Data variants take the raw call payload, so they serve both a conversation's
// additional_attributes.phone blob and a row from the phone_calls API.
export const isMissedCallData = (phone = {}) => {
  const { direction, missed, unanswered_missed: unansweredMissed } = phone;
  if (direction !== 'inbound') return false;
  return isFlagSet(missed) || isFlagSet(unansweredMissed);
};

// A missed call the caller never got a follow-up for — the worse of the two.
export const isUnansweredMissedCallData = (phone = {}) => {
  const { direction, unanswered_missed: unansweredMissed } = phone;
  return direction === 'inbound' && isFlagSet(unansweredMissed);
};

export const isMissedCall = conversation =>
  isMissedCallData(getPhoneCallData(conversation));

export const isUnansweredMissedCall = conversation =>
  isUnansweredMissedCallData(getPhoneCallData(conversation));

const digitsOnly = value => String(value || '').replaceAll(/\D/g, '');

// Phone contacts are auto-created named after the number that called, so a
// "name" that is really just that number carries no information. Compare digits
// only: the contact may be stored as +421903107997 while the call reports
// 421903107997. Short numbers (extensions, feature codes) are ignored so a
// stray digit run inside a genuine name cannot hide it.
export const hasKnownContactName = (name, ...numbers) => {
  if (!name) return false;
  const nameDigits = digitsOnly(name);
  if (!nameDigits) return true;
  return !numbers.some(number => {
    const numberDigits = digitsOnly(number);
    return numberDigits.length >= 6 && nameDigits.includes(numberDigits);
  });
};

// --- Phone number / dial-string validation ---------------------------------

// Spaces, dashes, parentheses and slashes are cosmetic separators — strip them
// before validating so "+421 903 107 997" and "+421903107997" behave the same.
const normalizePhone = phone => phone.replaceAll(/[\s\-()/]/g, '').trim();

// A dialable number: an optional prefix followed by at least 9 digits/spaces.
// The prefix is either a *-form routing code (*123 or 17*) or a leading +,
// never both — a number containing * must not also contain +.
// example: *123421903123456, +421903123456 or 17*0042077770002493
// eslint-disable-next-line security/detect-unsafe-regex -- false positive: each prefix branch is anchored by a required * or + literal, no catastrophic backtracking
const NUMBER_REGEX = /^(\*\d+|\d+\*|\+)?[\d\s]{9,}$/;
// A standalone star / feature code, e.g. *123.
const FEATURE_REGEX = /^\*\d+$/;
// An internal extension — 3 or more digits.
const EXTENSION_REGEX = /^\d{3,}$/;

// True when `phone` is a dialable number, feature code or extension.
export const isValidPhoneNumber = phone => {
  if (!phone) return false;
  const normalized = normalizePhone(phone);
  return (
    NUMBER_REGEX.test(normalized) ||
    EXTENSION_REGEX.test(normalized) ||
    FEATURE_REGEX.test(normalized)
  );
};

// A routing code typed in front of the number, e.g. the 17* of 17*00420777002493.
const ROUTING_PREFIX_REGEX = /^\d+\*/;

// Builds the dial string that routes `number` through an inbox's dialing
// prefix. The PBX reads + inside a routed number as a feature code, so an
// international number goes out as 00…; a routing code already typed in front
// of the number is replaced, not stacked.
export const applyDialingPrefix = (number, prefix) => {
  if (!prefix) return number;
  const bareNumber = normalizePhone(number)
    .replace(ROUTING_PREFIX_REGEX, '')
    .replace(/^\+/, '00');
  return `${prefix}${bareNumber}`;
};
